"use strict";
/**
 * Нормализация своего веб-домена тенанта (эпик CUSTDOM, docs/todo-custdom.md).
 *
 * Одна функция на обе стороны: админка нормализует ввод до отправки, ядро — до записи в
 * `core.tenant_custom_domains` и до лукапа подсказки на странице входа. Сравнение хостов —
 * точное, поэтому расхождение нормализаций между UI и ядром означало бы «домен заведён,
 * а не находится».
 *
 * ⚠ Веб-домен, не SIP: realm тенанта обязан совпадать с его id, иначе не сойдётся HA1.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeCustomDomainHosts = exports.normalizeCustomDomainHost = void 0;
const urlConstructor = () => {
    const ctor = globalThis.URL;
    if (!ctor)
        throw new Error('normalizeCustomDomainHost: глобальный URL недоступен в этой среде');
    return ctor;
};
/**
 * Метка DNS после IDNA: буквы/цифры/дефис, без дефиса по краям, до 63 символов. Без lookbehind:
 * пакет едет в браузерный бандл без транспиляции регэкспов, а Safari до 16.4 lookbehind не парсит —
 * упал бы на загрузке весь модуль контрактов, то есть вся админка.
 */
const DNS_LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
/** Четыре десятичных октета — то, во что WHATWG-парсер превращает любой числовой хост. */
const DOTTED_IPV4 = /^\d{1,3}(?:\.\d{1,3}){3}$/;
/**
 * Строка → нормализованный хост или причина отказа.
 *
 * - trim, нижний регистр, punycode (через WHATWG URL), срез завершающей точки;
 * - отказ, если в строке есть что-то кроме хоста: схема, порт, путь, query, креды, пробелы;
 * - IPv4-литерал допустим (закрытый контур открывают по IP), IPv6 — нет;
 * - масок нет: `*` не хост.
 */
const normalizeCustomDomainHost = (raw) => {
    const trimmed = raw.trim();
    if (trimmed === '')
        return { ok: false, reason: 'empty' };
    if (trimmed.startsWith('[') || /^[0-9a-f]*:[0-9a-f:]*:[0-9a-f:.]*$/i.test(trimmed))
        return { ok: false, reason: 'ipv6' };
    // Всё, что делает строку не-хостом, отбиваем ДО парсера: URL молча срезал бы путь/порт/креды,
    // и «callc.ru:8443/x» записался бы как «callc.ru».
    if (/[\s/\\?#@:*%]/.test(trimmed))
        return { ok: false, reason: 'not_a_host' };
    // Конструктор — до try: его отсутствие это сбой среды, он не должен превратиться в `not_a_host`.
    const UrlCtor = urlConstructor();
    const hostname = (() => {
        try {
            return new UrlCtor(`http://${trimmed}`).hostname;
        }
        catch {
            // Невалидный хост для WHATWG-парсера — это ответ на вопрос, а не сбой: отказ с причиной.
            return null;
        }
    })();
    if (hostname === null)
        return { ok: false, reason: 'not_a_host' };
    const host = hostname.endsWith('.') ? hostname.slice(0, -1) : hostname;
    if (host === '')
        return { ok: false, reason: 'not_a_host' };
    // Парсер молча переписывает числовые хосты в IPv4: «999» → «0.0.3.231», «10.1» → «10.0.0.1».
    // Админ, сохранив одно, увидел бы в списке другое — принимаем IPv4 только записанным как есть.
    if (DOTTED_IPV4.test(host) && host !== trimmed.toLowerCase().replace(/\.$/, '')) {
        return { ok: false, reason: 'not_a_host' };
    }
    if (host.length > 253)
        return { ok: false, reason: 'too_long' };
    if (!host.split('.').every((label) => DNS_LABEL.test(label)))
        return { ok: false, reason: 'not_a_host' };
    return { ok: true, host };
};
exports.normalizeCustomDomainHost = normalizeCustomDomainHost;
/**
 * Список строк → нормализованный набор без дублей (порядок первого появления) или первый отказ
 * вместе с исходной строкой — чтобы показать админу, какая из строк кривая.
 */
const normalizeCustomDomainHosts = (raws) => {
    const hosts = [];
    for (const raw of raws) {
        const result = (0, exports.normalizeCustomDomainHost)(raw);
        if (!result.ok)
            return { ok: false, raw, reason: result.reason };
        if (!hosts.includes(result.host))
            hosts.push(result.host);
    }
    return { ok: true, hosts };
};
exports.normalizeCustomDomainHosts = normalizeCustomDomainHosts;
//# sourceMappingURL=custom-domain.js.map